import { useContext, useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import InputText from "../Inputs/InputText";
import InputFile from "../Inputs/InputFile";
import { useLanguage } from "../../context/LanguageContext";
import { AuthContext } from "../../context/context";
import InputCurrency from "../Inputs/InputCurrency";
import PropTypes from "prop-types";

export default function AddWishForm({
  handleSubmitWish,
  formRef,
  submitHandlerRef,
  defaultValues = {},
}) {
  const { copy } = useLanguage();
  const { userData: authUserData } = useContext(AuthContext);

  const AddWishSchema = z.object({
    wish_title: z.string().min(1, "Title is required").max(35, "Title must be 35 characters or less"),
    wish_img: z
      .any()
      .refine((files) => {
        return files !== null && files !== undefined;
      }, copy.card_wishlist.errors.img_required)
      .refine((files) => {
        if (typeof files === "string") return true;

        if (Array.isArray(files)) {
          const file = files[0];
          return file?.size < 5 * 1024 * 1024;
        }

        return true;
      }, copy.card_wishlist.errors.img_too_large),
    wish_url: z.string().min(1, copy.card_wishlist.errors.url_required).max(2000, "URL is too long (max 2000 characters)").refine((val) => val.startsWith("http"), copy.card_wishlist.errors.url_invalid),
    wish_price: z.preprocess(
      (val) => (val === "" ? undefined : Number(val)),
      z
        .number({ required_error: "Price is required" })
        .min(0.01, { message: "Price must be greater than 0" })
    ),
    wish_currency: z.string().min(1, "Currency is required"),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(AddWishSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      wish_title: defaultValues.wish_title || "",
      wish_img: defaultValues.wish_img || null,
      wish_url: defaultValues.wish_url || "",
      wish_price: defaultValues.wish_price || "",
      wish_currency: defaultValues.wish_currency || "",
    },
  });

  const allCurrencies = authUserData?.currencies || [
    "EUR",
    "USD",
    "PLN",
    "DKK",
    "GBP",
    "CAD",
    "AUD",
    "CHF",
    "JPY",
    "SEK",
    "NOK",
  ];

  const setCurrency = useCallback(
    (currency) => setValue("wish_currency", currency, { shouldValidate: true, shouldDirty: true }),
    []
  );

  const onSubmit = (data) => {
    handleSubmitWish(data);
  };

  const onError = (errors) => {
    console.error("Validation errors:", errors);
  };

  if (submitHandlerRef) {
    submitHandlerRef.current = handleSubmit(onSubmit, onError);
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      ref={formRef}
      className="flex flex-col gap-3"
    >
      <InputText
        label={copy.card_wishlist.form.label_title}
        placeholder={copy.card_wishlist.form.placeholder_title}
        name="wish_title"
        {...register("wish_title")}
        error={errors.wish_title?.message}
        details={`${watch("wish_title").length}/35 characters`}
        maxLength={35}
      />

      <InputFile
        label={copy.card_wishlist.form.label_img}
        name="wish_img"
        {...register("wish_img")}
        onChange={(e) => {
          setValue("wish_img", e.target.files, { shouldValidate: true });
        }}
        error={errors.wish_img?.message}
      />

      <div className="grid md:grid-cols-3 grid-cols-1 gap-2 w-full">
        <InputText
          label={copy.card_wishlist.form.label_link}
          placeholder={copy.card_wishlist.form.placeholder_link}
          name="wish_url"
          {...register("wish_url")}
          error={errors.wish_url?.message}
        />

        <InputText
          label={copy.card_wishlist.form.label_price}
          placeholder={copy.card_wishlist.form.placeholder_price}
          name="wish_price"
          {...register("wish_price")}
          error={errors.wish_price?.message}
        />

        <InputCurrency
          value={watch("wish_currency")}
          label={copy.card_wishlist.form.label_currency}
          placeholder={copy.card_wishlist.form.placeholder_currency}
          error={errors.wish_currency?.message}
          currencies={allCurrencies}
          setCurrency={setCurrency}
          onChange={(e) => {
            setValue("wish_currency", e.target.value, { shouldValidate: true, shouldDirty: true });
          }}
          {...register("wish_currency")}
        />
      </div>
    </form>
  );
}

AddWishForm.propTypes = {
  handleSubmitWish: PropTypes.func.isRequired,
  formRef: PropTypes.object.isRequired,
  submitHandlerRef: PropTypes.object,
  defaultValues: PropTypes.object,
};
